import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import './Registreren.css';

const EMPTY = {
  firstName: '', lastName: '', phone: '', email: '', dateOfBirth: '',
  address: '', postalCode: '', city: '',
  bedrijfsnaam: '', kvkNumber: '', btwNumber: '',
};

const HINTS = {
  id_voor: 'Paspoort, ID-kaart of rijbewijs',
  id_achter: 'Alleen bij ID-kaart of rijbewijs',
  kvk: 'Uittreksel uit het Handelsregister',
  cert_vca: 'VCA Basis of VCA VOL',
  cert_gas: 'Alleen als je dit certificaat hebt',
};

// Telefoonfoto's zijn vaak 3–8 MB. Verklein ze vóór het uploaden naar max 2200 px JPEG.
// Lukt dat niet (bijv. HEIC buiten Safari), dan gaat het origineel mee.
async function shrinkImage(file) {
  if (!file || !file.type.startsWith('image/') || file.size < 1.2 * 1024 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2200 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function Field({ label, name, value, onChange, error, ...rest }) {
  return (
    <label className={`rg-field ${error ? 'has-error' : ''}`}>
      <span>{label}</span>
      <input name={name} value={value} onChange={onChange} {...rest} />
    </label>
  );
}

function FileCard({ f, file, date, onFile, onDate, missing }) {
  const inputRef = useRef(null);
  const isImg = file && file.type.startsWith('image/');
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!isImg) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImg]);

  return (
    <div className={`rg-file ${file ? 'filled' : ''} ${missing ? 'has-error' : ''}`}>
      <div className="rg-file-head">
        <div>
          <strong>{f.label}</strong>
          <small>{f.required ? 'Verplicht' : 'Optioneel'}{HINTS[f.name] ? ` · ${HINTS[f.name]}` : ''}</small>
        </div>
        {file && (
          <button type="button" className="rg-file-remove" onClick={() => onFile(null)} aria-label={`${f.label} verwijderen`}>
            Verwijderen
          </button>
        )}
      </div>

      {file ? (
        <div className="rg-file-chosen">
          {preview ? <img src={preview} alt="" /> : <span className="rg-file-pdf">PDF</span>}
          <span className="rg-file-name">{file.name}</span>
        </div>
      ) : (
        <button type="button" className="rg-file-pick" onClick={() => inputRef.current?.click()}>
          Foto maken of bestand kiezen
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => { onFile(e.target.files?.[0] || null); e.target.value = ''; }}
      />

      {f.cert && file && (
        <label className={`rg-field rg-field--date ${missing === 'date' ? 'has-error' : ''}`}>
          <span>Geldig tot</span>
          <input type="date" value={date} onChange={(e) => onDate(e.target.value)} />
        </label>
      )}
    </div>
  );
}

export default function Registreren() {
  const { token } = useParams();
  const [state, setState] = useState('laden'); // laden | open | gebruikt | verlopen | ongeldig | klaar
  const [fileFields, setFileFields] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({});
  const [dates, setDates] = useState({});
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    fetch(`/api/public/registratie/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => { setFileFields(d.files || []); setState(d.status || 'ongeldig'); })
      .catch(() => setState('fout'));
  }, [token]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    for (const k of Object.keys(EMPTY)) if (!form[k].trim()) e[k] = true;
    if (form.kvkNumber && !/^\d{8}$/.test(form.kvkNumber.replace(/\s/g, ''))) e.kvkNumber = true;
    if (form.btwNumber && !/^NL\d{9}B\d{2}$/i.test(form.btwNumber.replace(/\s/g, ''))) e.btwNumber = true;
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = true;
    for (const f of fileFields) {
      if (f.required && !files[f.name]) e[f.name] = true;
      if (f.cert && files[f.name] && !dates[f.name]) e[f.name] = 'date';
    }
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      setMessage('Nog niet alles is ingevuld. Kijk naar de rode velden.');
      document.querySelector('.has-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setMessage('');
    const fd = new FormData();
    for (const [k, v] of Object.entries(form)) fd.append(k, v.trim());
    for (const f of fileFields) {
      if (files[f.name]) fd.append(f.name, files[f.name]);
      if (f.cert && dates[f.name]) fd.append(`${f.name}_geldig_tot`, dates[f.name]);
    }

    // XHR i.p.v. fetch: dan kunnen we de uploadvoortgang tonen.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/public/registratie/${encodeURIComponent(token)}`);
    xhr.upload.onprogress = (p) => { if (p.lengthComputable) setProgress(Math.round((p.loaded / p.total) * 100)); };
    xhr.onload = () => {
      setProgress(null);
      let d = {};
      try { d = JSON.parse(xhr.responseText); } catch { /* geen json */ }
      if (xhr.status === 201) { setState('klaar'); window.scrollTo(0, 0); return; }
      if (xhr.status === 410) { setState('gebruikt'); return; }
      setMessage(d.error || 'Versturen mislukt. Probeer het opnieuw.');
    };
    xhr.onerror = () => { setProgress(null); setMessage('Geen verbinding. Controleer je internet en probeer opnieuw.'); };
    setProgress(0);
    xhr.send(fd);
  };

  const shell = (children) => (
    <div className="rg-page">
      <header className="rg-top"><img src="/assets/logo.png" alt="Özel Uitzendbureau" /></header>
      <main className="rg-main">{children}</main>
    </div>
  );

  if (state === 'laden') return shell(<p className="rg-muted">Laden…</p>);
  if (state === 'klaar') {
    return shell(
      <div className="rg-done">
        <h1>Bedankt, {form.firstName}!</h1>
        <p>Je gegevens zijn goed aangekomen. We bekijken je aanmelding en nemen contact met je op. Na goedkeuring krijg je een link om je eigen account aan te maken.</p>
      </div>
    );
  }
  if (state !== 'open') {
    const text = {
      gebruikt: 'Deze link is al gebruikt. Je registratie is al binnen.',
      verlopen: 'Deze link is verlopen. Vraag Özel om een nieuwe link.',
    }[state] || 'Deze link werkt niet. Vraag Özel om een nieuwe link.';
    return shell(<div className="rg-done"><h1>Link niet geldig</h1><p>{text}</p></div>);
  }

  const busy = progress !== null;
  const fieldProps = (name) => ({ name, value: form[name], onChange, error: errors[name] });

  return shell(
    <form className="rg-form" onSubmit={submit} noValidate>
      <div className="rg-intro">
        <h1>Aanmelden als medewerker</h1>
        <p>Vul je gegevens in en upload je documenten. Dit duurt ongeveer 5 minuten. Foto's maken met je telefoon mag.</p>
      </div>

      <section className="rg-section">
        <h2><span>1</span>Persoonlijke gegevens</h2>
        <div className="rg-grid">
          <Field label="Voornaam" {...fieldProps('firstName')} autoComplete="given-name" />
          <Field label="Achternaam" {...fieldProps('lastName')} autoComplete="family-name" />
          <Field label="Telefoonnummer" {...fieldProps('phone')} type="tel" inputMode="tel" placeholder="06 12345678" autoComplete="tel" />
          <Field label="E-mailadres" {...fieldProps('email')} type="email" inputMode="email" autoComplete="email" />
          <Field label="Geboortedatum" {...fieldProps('dateOfBirth')} type="date" autoComplete="bday" />
          <Field label="Adres (straat + huisnummer)" {...fieldProps('address')} autoComplete="street-address" />
          <Field label="Postcode" {...fieldProps('postalCode')} placeholder="1234 AB" autoComplete="postal-code" />
          <Field label="Plaats" {...fieldProps('city')} autoComplete="address-level2" />
        </div>
      </section>

      <section className="rg-section">
        <h2><span>2</span>Bedrijfsgegevens</h2>
        <div className="rg-grid">
          <Field label="Bedrijfsnaam" {...fieldProps('bedrijfsnaam')} autoComplete="organization" />
          <Field label="KvK-nummer" {...fieldProps('kvkNumber')} inputMode="numeric" placeholder="8 cijfers" />
          <Field label="Btw-nummer" {...fieldProps('btwNumber')} placeholder="NL123456789B01" autoCapitalize="characters" />
        </div>
      </section>

      <section className="rg-section">
        <h2><span>3</span>Documenten</h2>
        <p className="rg-muted">Foto of PDF, maximaal 10 MB per bestand.</p>
        <div className="rg-files">
          {fileFields.map((f) => (
            <FileCard
              key={f.name}
              f={f}
              file={files[f.name]}
              date={dates[f.name] || ''}
              missing={errors[f.name]}
              onFile={async (file) => {
                const small = await shrinkImage(file);
                if (small && small.size > 10 * 1024 * 1024) { setMessage(`${f.label}: bestand is groter dan 10 MB.`); return; }
                setFiles((x) => ({ ...x, [f.name]: small }));
              }}
              onDate={(d) => setDates((x) => ({ ...x, [f.name]: d }))}
            />
          ))}
        </div>
      </section>

      {message && <p className="rg-error" role="alert">{message}</p>}

      <button type="submit" className="rg-submit" disabled={busy}>
        {busy ? `Versturen… ${progress}%` : 'Gegevens versturen'}
      </button>
      {busy && <div className="rg-progress"><div style={{ width: `${progress}%` }} /></div>}
      <p className="rg-privacy">Je gegevens worden alleen gebruikt door Özel Uitzendbureau voor je inschrijving en opdrachten.</p>
    </form>
  );
}
